import React,{FC, useEffect, useState, useMemo} from "react";
import { Card, Spinner} from "react-bootstrap";
import { getHistoricalTradeData } from "../core/_request";
import { getNameByISO3, shortenLargeNumber } from "../../../../libs/misc";
import { useLoading } from "../../../../contexts/LoadingContext";
import TradePartnerDropdown from "./TradePartnerDropdown";
import { ASEAN_MEMBER } from "../../../../libs/constant";
import ChartWithShare from "../../../../../ui-kit/ChartWithShare";

interface ExtendedHighchartCreditInterface{
  credits:any
}
type CommodityLineChartCardProps = {
  params:{
    rep: string;
    par: string;
    tradeflow:string;
    product_group: any;
    hs_ver: string;
  }
  product_group?: any;
}

const CommodityLineChartCard: FC<CommodityLineChartCardProps> = ({
  params,
  product_group = [],
}) => {
  const highchartOptions = {
    chart: {
      type: 'line',
      marginTop: 20,
      events: {
        load: function (this: ExtendedHighchartCreditInterface) {
          this.credits.element.onclick = function () {
            window.open('http://comtrade.un.org', '_blank');
          };
        }
      }
    },
    tooltip: {
      shared: true,
      crosshairs: true,
    },
    title: {
      text: ''
    },
    xAxis: [{
        crosshair: true,
        tickInterval: 1
      }],
    yAxis: [{
      labels: {
        formatter: function (this:any) { 
          return shortenLargeNumber(this.value); 
        },
      },
      title: {
        text: `Value in (USD)`,
      }
    }],
    plotOptions: {
      areaspline: {
          stacking: 'normal',
      },
      series:{
        connectNulls: true,
      }
    },
    credits: {
      enabled: true,
      text: 'Data source: UN Comtrade',
      href: '#',
      position: {
        align: 'center'
      }
    },
    exporting: {
      enabled: true
    },
    accessibility: {
      enabled: false
    }
  };
  const { registerStart, registerEnd } = useLoading();
  const [isLoading, setIsLoading] = useState(true)
  const [historicalDataChartOptions, setHistoricalDataChartOptions] = useState<any>(highchartOptions)
  const [mainTitle, subTitle] = useMemo(() => {
    const findPG = (id: number, list: any[]): any | null => {
      for (const item of list) {
        if (item.id === id) return item;
        if (item.sub_category?.length) {
          const found = findPG(id, item.sub_category);
          if (found) return found;
        }
      }
      return null;
    };

    const flowLabel = params.tradeflow === 'imports' ? 'Import' : 'Export'
    let titleSuffix = ''
    const selectedPGs = params.product_group || []
    if(selectedPGs.length === 1){
      const selectedPG = findPG(+selectedPGs[0], product_group);
      if(selectedPG?.sub_category?.length){
        titleSuffix = `${selectedPG.name} (Detail)`
      } else {
        titleSuffix = selectedPG?.name || 'Product Group'
      }
    } else {
      const selectedNames = selectedPGs.map((pgId: any) => {
        const found = findPG(+pgId, product_group);
        return found?.name || ''
      }).filter(Boolean)
      titleSuffix = selectedNames.length > 0
        ? selectedNames.join(', ')
        : 'Product Group'
    }
    const main = `${getNameByISO3(params.rep) || 'ASEAN'}'s Historical ${flowLabel} Value by Each Member`
    return [main, titleSuffix];
  }, [params.rep, params.tradeflow, params.product_group, product_group])

  const graphTitle = `${mainTitle} - ${subTitle}`;

  const fetchHistoricalData = async (
    params: any
  ) => {
    const rep = params.rep === 'ASEAN' ? ASEAN_MEMBER : [params.rep];
    const results: any[] = [];
    for (const pg of params.product_group || []) {
      for(const repSingle of rep){
        try {
          const res = await getHistoricalTradeData({
            rep:repSingle,
            par: params.par || 'WLD',
            tradeflow: params.tradeflow,
            productGroup: pg,
          });
          results.push(res.data?.data);
        } catch (error) {
          console.error(`Error fetching ${pg}:`, error);
        }
      }
    }
    const flow = params.tradeflow==='imports'?'1':'2'
    const series: any[] = [];

    // reporter -> (year -> { val, net })
    const byRep = new Map<string, Map<number, { val: number; net: number }>>();
    // Keep a global set of all years so we output aligned x-axis
    const allYears = new Set<number>();

    results.forEach((res: any) => {
      const tradeData = res.history_data?.[flow] ?? [];
      const years = res.years_data ?? [];
      const rep = tradeData[0].rep;
      years.forEach((y: number) => allYears.add(y));

      if (!byRep.has(rep)) byRep.set(rep, new Map());
      const yearIndex = byRep.get(rep)!;

      // Sum per (reporter, year)
      tradeData.forEach((row: any) => {
        const y = row.year;
        if (!yearIndex.has(y)) yearIndex.set(y, { val: 0, net: 0 });
        const bucket = yearIndex.get(y)!;
        bucket.val += Number(row.val ?? 0);
        bucket.net += Number(row.netWeight ?? 0);
      });
    });

    // Now build/update one series per reporter
    const sortedYears = Array.from(allYears).sort((a, b) => a - b);

    byRep.forEach((yearIndex, rep) => {
      const seriesName = `${getNameByISO3(rep)}`;

      // try reuse existing series
      let obj = series.find((s) => s.name === seriesName);
      if (!obj) {
        obj = {
          name: seriesName,
          type: "areaspline",
          tooltip: { valueSuffix: " US$" }, // change to ' kg' if you build KG series
          data: []
        };
        series.push(obj);
      }

      // rebuild data aligned to all years
      obj.data = sortedYears
        .map((y) => ({
          x: y,
          y: yearIndex.get(y)?.val ?? 0, // <-- USD; use `.net` for KG tab
        }))
    });

    const validYears = new Set(sortedYears);
    series.forEach((s: any) => {
      s.data.forEach((d: any) => {
        if (d.y === 0) validYears.delete(d.x);
      });
    });

    setHistoricalDataChartOptions({
      ...historicalDataChartOptions,
      series: series.map((s: any) => ({
        ...s,
        data: s.data.filter((d: any) => validYears.has(d.x) && d.y !== 0)
      })).filter((s: any) => s.data.length > 0),
    })

  };

  useEffect(()=>{
    if (!params?.rep) return;
    registerStart();
    setIsLoading(true);
    fetchHistoricalData(params)
    .finally(()=>{
      setIsLoading(false);
      registerEnd();
    })
  },[params, registerStart, registerEnd])

  // const handleSelectPar = (econ:string)=>{
  //   console.log(econ)
  // }
  return (
    <>
        <Card className="card-custom">
          <>
            <Card.Header className="card-header-stretch d-flex align-items-center justify-content-between">
              <Card.Title
                title={graphTitle}
                style={{
                  maxWidth: '80%',
                }}
              >
                <div className="d-flex flex-column">
                  <span className="fw-bold">{mainTitle}</span>
                  <span className="text-muted fs-7 fw-normal mt-1">{subTitle}</span>
                </div>
              </Card.Title>
              {/* <TradePartnerDropdown onSelect={handleSelectPar}/> */}
            </Card.Header>
            <Card.Body style={{minHeight:'300px'}}>
              
            {!isLoading ? (
              <ChartWithShare chartOptions={historicalDataChartOptions} title={graphTitle}/>
              // <HighchartsReact
              //   highcharts={Highcharts}
              //   options={historicalDataChartOptions}
              // />
            ):(
              <div className="d-flex align-items-center justify-content-center" style={{ height: '50vh' }}>
                <Spinner animation="border" role="status" variant="primary" className="me-3" />
                <div className="loading-text">
                  Loading
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                  <span className="dot">.</span>
                </div>
              </div>
            )}
            </Card.Body>
          </>
        </Card>  
    </>
  )
}

export {CommodityLineChartCard}